/**
 * AchievementsBadgeWall — V141.6.
 *
 * Renders the 5 milestone badges in the workspace dashboard. Locked
 * badges show greyed out; unlocked ones glow gold + show unlock date.
 *
 * Polls /api/achievements/{workspaceId} on mount + when the trial
 * progress bar fires its onAction event. Cheap (1 DB hit per poll).
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "../../config";
import { Trophy, Zap, Target, Crown, Award, Lock } from "lucide-react";

const MILESTONES = [
  { id: "first_run",       icon: Zap,    label: "Prvý beh",       desc: "1. akcia" },
  { id: "ten_actions",     icon: Target, label: "10 akcií",       desc: "Top 12%" },
  { id: "half_trial",      icon: Award,  label: "Polovica trialu",desc: "Day 7+" },
  { id: "first_paid",      icon: Crown,  label: "Paid mesiac",    desc: "Production" },
  { id: "century_actions", icon: Trophy, label: "100 akcií",      desc: "Power user · Top 1%" },
];

export default function AchievementsBadgeWall({ workspaceId, accent = "#FFB800" }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const fetch = async () => {
      try {
        const r = await axios.get(`${API}/achievements/${workspaceId}`);
        if (!cancelled) setData(r.data);
      } catch (e) { /* silent — badge wall hidden if endpoint down */ }
    };
    fetch();
    return () => { cancelled = true; };
  }, [workspaceId]);

  if (!data) return null;
  const unlockedSet = new Set(data.unlocked || []);

  return (
    <section
      className="mb-6 border border-gray-800 bg-neutral-950 p-4 sm:p-5"
      data-testid="achievements-badge-wall"
    >
      <p
        className="text-[10px] uppercase tracking-[0.2em] font-black mb-3"
        style={{ color: accent }}
      >
        🏆 Achievements · {unlockedSet.size}/{MILESTONES.length}
      </p>
      <div className="grid grid-cols-5 gap-2">
        {MILESTONES.map((m) => {
          const ok = unlockedSet.has(m.id);
          const Icon = ok ? m.icon : Lock;
          return (
            <div
              key={m.id}
              data-testid={`achievement-${m.id}-${ok ? "unlocked" : "locked"}`}
              className={`p-3 text-center border transition-colors ${
                ok
                  ? "border-amber-500 bg-amber-500/10"
                  : "border-gray-800 bg-black/40"
              }`}
              title={m.desc}
            >
              <Icon
                className={`w-5 h-5 mx-auto mb-1 ${
                  ok ? "text-amber-400" : "text-gray-700"
                }`}
              />
              <p
                className={`text-[10px] font-black uppercase tracking-tight leading-tight ${
                  ok ? "text-white" : "text-gray-600"
                }`}
              >
                {m.label}
              </p>
              <p
                className={`text-[9px] mt-0.5 ${
                  ok ? "text-amber-300" : "text-gray-700"
                }`}
              >
                {m.desc}
              </p>
            </div>
          );
        })}
      </div>
    </section>
  );
}
